export type AvatarRole = "player" | "npc";
export type RoomJoinMode = "open" | "members" | "password" | "token";

export type DoorDirection = "north" | "south" | "east" | "west";
export type RoomLayoutState = "draft" | "published" | "archived";
export type RoomLayoutAccess = "free" | "premium" | "unlockable";
export type RoomThemeState = "draft" | "published" | "archived";
export type RoomThemeAccess = "free" | "premium" | "unlockable";
export type FurnitureInteractionType =
  | "none"
  | "sit"
  | "lay"
  | "door"
  | "teleport";
export type FurnitureItemState = "published" | "draft";

export interface FurnitureColorVariant {
  id: string;
  name: string;
  colors: Record<string, string>;
  priceCoins?: number;
  active?: boolean;
}

export interface RoomLayoutDoor {
  x: number;
  z: number;
  direction: DoorDirection;
}

export interface RoomLayoutTile {
  walkable: boolean;
  height: number;
  // Reserved metadata for future tile semantics (stairs/terrain/triggers).
  surface?: "floor" | "stairs";
}

export interface RoomLayoutWallEdge {
  x: number;
  z: number;
  direction: DoorDirection;
}

export interface RoomLayoutShape {
  width: number;
  height: number;
  tiles: RoomLayoutTile[][];
  door: RoomLayoutDoor;
  // If omitted, renderer derives an exterior contour automatically.
  walls?: RoomLayoutWallEdge[];
}

export interface RoomLayoutRuntime extends RoomLayoutShape {
  id: string;
  name: string;
  access: RoomLayoutAccess;
  maxUsers: number;
}

export interface RoomLayoutSummary extends RoomLayoutShape {
  id: string;
  name: string;
  slug: string;
  state: RoomLayoutState;
  access: RoomLayoutAccess;
  maxUsers: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface RoomLayoutOption extends RoomLayoutSummary {
  requiresUnlock: boolean;
  unlocked: boolean;
}

export interface RoomThemeColors {
  wall: string;
  floorTop: string;
  floorSide: string;
  porchMarker: string;
}

export interface RoomThemeRuntime extends RoomThemeColors {
  id: string;
  name: string;
  access: RoomThemeAccess;
  wallThemeId?: string;
  wallThemeName?: string;
  floorThemeId?: string;
  floorThemeName?: string;
}

export interface RoomThemeSummary extends RoomThemeColors {
  id: string;
  name: string;
  slug: string;
  state: RoomThemeState;
  access: RoomThemeAccess;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface RoomThemeOption extends RoomThemeSummary {
  requiresUnlock: boolean;
  unlocked: boolean;
}

export interface AvatarState {
  id: string;
  address: string;
  name: string;
  role: AvatarRole;
  isBot: boolean;
  color: string;
  isDancing?: boolean;
  isSitting?: boolean;
  sittingAssetId?: string;
  sittingSeatIndex?: number;
  waveAt?: number;
  x: number;
  y: number;
  z: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  facing: number;
  path?: [number, number][];
  lastMessage?: string;
  lastMessageAt?: number;
}

export interface AvatarMetadataState {
  id: string;
  address: string;
  name: string;
  role: AvatarRole;
  isBot: boolean;
  color: string;
  isDancing?: boolean;
  isSitting?: boolean;
  sittingAssetId?: string;
  sittingSeatIndex?: number;
  waveAt?: number;
  lastMessage?: string;
  lastMessageAt?: number;
}

// Desired spawn anchor. Runtime clamps this into each room's valid grid.
// Negative X biases toward the left wall; Z=0 keeps it centered on depth.
export const DOOR_CELL: [number, number] = [-9, 0];
// Offset from the clamped door cell so avatars spawn within door depth.
export const DOOR_SPAWN_X_OFFSET = -0.61;

export interface RoomAsset {
  id: string;
  roomId: string;
  type: "glb" | "frame";
  url: string;
  name?: string;
  thumbnail?: string;
  mediaMime?: string;
  ownerAddress?: string;
  faContract?: string;
  tokenId?: string;
  x: number;
  y: number;
  z: number;
  size: number;
  rotation?: number;
  frameWidth?: number;
  frameHeight?: number;
  furnitureId?: string;
  tintColor?: string;
  blockWidth?: number;
  blockDepth?: number;
  sittable?: boolean;
  interactionType?: FurnitureInteractionType;
  walkableTop?: boolean;
  stackableTop?: boolean;
  stackHeight?: number;
  teleportGroup?: string;
  actionOpenAnimation?: string;
  actionCloseAnimation?: string;
  doorState?: "closed" | "open";
  teleportState?: "closed" | "open" | "effects";
  seatOffsetX?: number;
  seatOffsetY?: number;
  seatOffsetZ?: number;
  colorVariantId?: string;
  paletteColors?: Record<string, string>;
}

export interface FurnitureDefinition {
  id: string;
  name: string;
  description?: string;
  modelUrl: string;
  thumbnailUrl?: string;
  priceCoins: number;
  tintColor?: string;
  artist?: string;
  blockWidth: number;
  blockDepth: number;
  sittable: boolean;
  interactionType?: FurnitureInteractionType;
  walkableTop?: boolean;
  stackableTop?: boolean;
  stackHeight?: number;
  teleportGroup?: string;
  actionOpenAnimation?: string;
  actionCloseAnimation?: string;
  seatOffsetX?: number;
  seatOffsetY?: number;
  seatOffsetZ?: number;
  colorSlots?: string[];
  colorVariants?: FurnitureColorVariant[];
  defaultColorVariantId?: string;
  catalogCategory?: string;
  catalogCollection?: string;
  itemState?: FurnitureItemState;
  sandboxOwnerAddress?: string;
  createdByDisplayName?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface UserFurnitureInventoryItem {
  furnitureId: string;
  quantity: number;
  placedQuantity?: number;
  availableQuantity?: number;
  colorVariantId?: string;
  paletteColors?: Record<string, string>;
}

export interface CoinPackageDefinition {
  id: string;
  name: string;
  coins: number;
  tezAmountMutez: string;
}

export interface RoomStatePayload {
  roomId: string;
  roomType: RoomType;
  layout: RoomLayoutRuntime;
  theme: RoomThemeRuntime;
  players: AvatarState[];
  bots: AvatarState[];
  assets: RoomAsset[];
  waitlistSize: number;
  capacity: number;
}

export interface RoomStateDeltaPayload {
  roomId: string;
  roomType: RoomType;
  layout: RoomLayoutRuntime;
  theme: RoomThemeRuntime;
  players: AvatarMetadataState[];
  bots: AvatarMetadataState[];
  removedPlayerIds: string[];
  removedBotIds: string[];
  /** When true, `players` and `bots` represent complete metadata snapshots. */
  fullSync?: boolean;
  waitlistSize: number;
  capacity: number;
}

export interface AvatarMotionState {
  id: string;
  x: number;
  y: number;
  z: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  facing: number;
}

export type AvatarMotionIntentKind = "start_move" | "turn" | "stop_move";

export interface AvatarMotionIntentState {
  id: string;
  kind: AvatarMotionIntentKind;
  x: number;
  y: number;
  z: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  facing: number;
}

export interface RoomStatePositionsPayload {
  roomId: string;
  roomType: RoomType;
  layout?: RoomLayoutRuntime;
  theme?: RoomThemeRuntime;
  players: AvatarMotionState[];
  bots: AvatarMotionState[];
  /** True when this is a correction snapshot, not the regular movement tick stream. */
  correction?: boolean;
  waitlistSize: number;
  capacity: number;
}

export interface RoomStateMotionIntentsPayload {
  roomId: string;
  roomType: RoomType;
  players: AvatarMotionIntentState[];
  bots: AvatarMotionIntentState[];
  waitlistSize: number;
  capacity: number;
}

export type ServerMessage =
  | {
      type: "init";
      selfId: string;
      roomId: string;
      waiting: boolean;
      waitlistPosition: number | null;
      capacity: number;
    }
  | {
      type: "state";
      state: RoomStatePayload;
      timestamp: number;
      seq: number;
    }
  | {
      type: "state_delta";
      state: RoomStateDeltaPayload;
      timestamp: number;
      seq: number;
    }
  | {
      type: "state_positions";
      state: RoomStatePositionsPayload;
      timestamp: number;
      seq: number;
    }
  | {
      type: "state_motion_intents";
      state: RoomStateMotionIntentsPayload;
      timestamp: number;
      seq: number;
    }
  | {
      type: "chat";
      fromId: string;
      fromName: string;
      role: AvatarRole;
      text: string;
      timestamp: number;
    }
  | {
      type: "waitlist";
      position: number;
    }
  | {
      type: "admitted";
    }
  | {
      type: "error";
      message: string;
    }
  | {
      type: "kicked";
      message: string;
    }
  | {
      type: "dm_notification";
      fromAddress: string;
      fromName: string;
      text?: string;
    }
  | {
      type: "friend_request_notification";
      fromAddress: string;
      fromName: string;
    }
  | {
      type: "idle_warning";
      secondsLeft: number;
    };

export type ClientMessage =
  | {
      type: "move";
      x: number;
      z: number;
      /** Optional cardinal turn intent (radians) for same-tile facing updates. */
      facing?: number;
    }
  | {
      type: "toggle_dance";
    }
  | {
      type: "Wave";
    }
  | {
      type: "chat";
      text: string;
    }
  | {
      type: "ask_bot";
      botId: string;
      prompt: string;
    }
  | {
      type: "kick_player";
      playerId: string;
    }
  | {
      type: "place_asset";
      asset: {
        id?: string;
        type: "glb" | "frame";
        url: string;
        name?: string;
        thumbnail?: string;
        mediaMime?: string;
        faContract?: string;
        tokenId?: string;
        x: number;
        y?: number;
        z: number;
        size?: number;
        rotation?: number;
        frameWidth?: number;
        frameHeight?: number;
        furnitureId?: string;
        tintColor?: string;
        blockWidth?: number;
        blockDepth?: number;
        sittable?: boolean;
        interactionType?: FurnitureInteractionType;
        walkableTop?: boolean;
        stackableTop?: boolean;
        stackHeight?: number;
        teleportGroup?: string;
        actionOpenAnimation?: string;
        actionCloseAnimation?: string;
        seatOffsetX?: number;
        seatOffsetY?: number;
        seatOffsetZ?: number;
        colorVariantId?: string;
        paletteColors?: Record<string, string>;
      };
    }
  | {
      type: "remove_asset";
      assetId: string;
    }
  | {
      type: "interact_asset";
      assetId: string;
    }
  | {
      type: "ping";
    };

export type RoomType = "home" | "town-hall" | "shop" | "gallery" | "museum";

export const ROOM_TYPE_CONFIG: Record<
  RoomType,
  { gridRadius: number; capacity: number; label: string; description: string }
> = {
  home: {
    gridRadius: 4,
    capacity: 8,
    label: "Home",
    description: "Your personal space",
  },
  "town-hall": {
    gridRadius: 8,
    capacity: 40,
    label: "Town Hall",
    description: "Public gathering place",
  },
  shop: {
    gridRadius: 5,
    capacity: 16,
    label: "The Shop",
    description: "Commerce and trading",
  },
  gallery: {
    gridRadius: 7,
    capacity: 20,
    label: "Objkt Gallery",
    description: "NFT art exhibition",
  },
  museum: {
    gridRadius: 7,
    capacity: 20,
    label: "Drobbit Museum",
    description: "Contemporary art museum",
  },
};

export interface RoomSummary {
  id: string;
  name: string;
  ownerAddress: string;
  capacity: number;
  isPrivate: boolean;
  joinMode: RoomJoinMode;
  tokenGateContract?: string;
  allowKnock: boolean;
  roomType: RoomType;
  layoutId: string;
  layoutName: string;
  layoutState: RoomLayoutState;
  layoutAccess: RoomLayoutAccess;
  // Legacy combined theme fields kept for compatibility.
  themeId: string;
  themeName: string;
  themeState: RoomThemeState;
  themeAccess: RoomThemeAccess;
  wallThemeId?: string;
  wallThemeName?: string;
  wallThemeState?: RoomThemeState;
  wallThemeAccess?: RoomThemeAccess;
  floorThemeId?: string;
  floorThemeName?: string;
  floorThemeState?: RoomThemeState;
  floorThemeAccess?: RoomThemeAccess;
  createdAt: string;
}

export interface FriendLink {
  userAddress: string;
  friendAddress: string;
  status: "pending" | "accepted";
  displayName?: string | null;
}

export interface ObjktToken {
  tokenId: string;
  faContract: string;
  artifactCid: string;
  artifactUrl: string;
  thumbnailUrl: string;
  name: string;
  ownedEditions?: number;
}

export interface ObjktPagination {
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface WalletNft {
  tokenId: string;
  faContract: string;
  name: string;
  mime?: string;
  thumbnailUrl: string;
  thumbnailMime?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  ownedEditions?: number;
}

export interface ObjktTokenBalance {
  faContract: string;
  tokenId: string;
  balance: number;
}

export interface ObjktContractBalance {
  faContract: string;
  balance: number;
}
